/**
 * KOPS-343: Integrated Search Experience
 * Combines AI Chat (HomePageV3) with Traditional Map Search (CustomerPortal)
 * Created: January 16, 2026
 */

import React, { useState, useEffect } from 'react';
import { Container, Button, ButtonGroup, Navbar, Row, Col, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComments, faMap, faRobot, faSearch } from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import HomePageV3 from './HomePageV3';
import CustomerPortal from '../customer-portal/CustomerPortal';
import './styles/search-with-ai.css';

type SearchMode = 'ai' | 'map';

interface SearchWithAIProps {
  defaultMode?: SearchMode;
}

// Agent profile interface
interface AgentProfile {
  name: string;
  photo: string;
  brokerage: string;
  phone: string;
  email: string;
  license: string;
}

// Copy of MinimalHeader from HomePageV3
const MinimalHeader: React.FC<{ agent?: AgentProfile }> = ({ agent }) => {
  return (
    <header className="minimal-header border-bottom" style={{ background: 'white', position: 'sticky', top: 0, zIndex: 1000 }}>
      <Container fluid className="py-2">
        <Row className="align-items-center">
          <Col>
            <div className="d-flex align-items-center">
              <h5 className="mb-0 me-3">chat.realestate</h5>
              <Badge bg="primary" className="me-2">AI-Powered</Badge>
              <small className="text-muted">Find homes by talking, not filtering</small>
            </div>
          </Col>
          {agent && (
            <Col xs="auto" className="d-flex align-items-center">
              <img 
                src={agent.photo} 
                alt={agent.name}
                className="rounded-circle me-2"
                style={{ width: '40px', height: '40px', objectFit: 'cover' }}
              />
              <div className="me-3">
                <div className="fw-bold small">{agent.name}</div>
                <div className="text-muted" style={{ fontSize: '0.75rem' }}>{agent.brokerage}</div>
              </div>
              <Button variant="primary" size="sm">Contact Agent</Button>
            </Col>
          )}
          <Col xs="auto" className="d-flex align-items-center">
            <div className="me-3">
              <Button variant="link" size="sm" className="text-muted">About</Button>
              <Button variant="link" size="sm" className="text-muted">How it works</Button>
              <Button variant="link" size="sm" className="text-muted">Sign in</Button>
            </div>
            {/* KairosIQ AI Realty Logo - Compliance */}
            <img
              src={require('../../assets/img/logos/KairosIQLogov2.jpg')}
              alt="KairosIQ AI Realty"
              style={{ height: '35px', objectFit: 'contain' }}
            />
          </Col>
        </Row>
      </Container>
    </header>
  );
};

const SearchWithAI: React.FC<SearchWithAIProps> = ({ defaultMode = 'ai' }) => {
  const [searchMode, setSearchMode] = useState<SearchMode>(defaultMode);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Capture initial query from navigation state
  const [initialQuery, setInitialQuery] = useState<string>('');
  
  // Agent profile - same as HomePageV3
  const [agentProfile] = useState<AgentProfile>({
    name: "Michael Boykin",
    photo: "https://placehold.co/100x100/1e3a8a/ffffff?text=MB",
    brokerage: "KairosIQ AI Realty",
    phone: "(555) 123-4567",
    email: "boykingroup@gmail.com",
    license: "AZ RE License #SA123456789"
  });

  // Check URL params for mode and initial query
  useEffect(() => {
    const mode = searchParams.get('mode');
    if (mode === 'map' || mode === 'ai') {
      setSearchMode(mode);
    }
    
    // Check for query in URL parameters first
    const urlQuery = searchParams.get('q');
    if (urlQuery) {
      setInitialQuery(urlQuery);
    }
    
    // Check if we have an initial query from navigation state
    const state = location.state as { initialQuery?: string };
    if (state?.initialQuery) {
      setInitialQuery(state.initialQuery);
      // Clear the state to prevent re-triggering
      navigate(location.pathname + location.search, { replace: true, state: {} });
    }
  }, [searchParams, location.state]);

  // Update URL when mode changes
  const handleModeChange = (mode: SearchMode) => {
    setSearchMode(mode);
    searchParams.set('mode', mode);
    setSearchParams(searchParams);
  };

  // Shared state for property results (to pass between modes)
  const [sharedPropertyResults, setSharedPropertyResults] = useState<any[]>([]);
  const [sharedSearchCriteria, setSharedSearchCriteria] = useState<any>(null);

  // White label context removed - using MinimalHeader with hardcoded values

  return (
    <div className="search-with-ai">
      {/* Use the same MinimalHeader as HomePageV3 */}
      <MinimalHeader agent={agentProfile} />

      {/* Header with mode switcher */}
      <div className="search-mode-header">
        <Container fluid>
          <div className="mode-switcher-container">
            <h1 className="search-title">Find Your Dream Home</h1>
            <ButtonGroup className="mode-switcher">
              <Button
                variant={searchMode === 'ai' ? 'primary' : 'outline-primary'}
                onClick={() => handleModeChange('ai')}
                className="mode-button"
              >
                <FontAwesomeIcon icon={faRobot} className="me-2" />
                AI Assistant
              </Button>
              <Button
                variant={searchMode === 'map' ? 'primary' : 'outline-primary'}
                onClick={() => handleModeChange('map')}
                className="mode-button"
              >
                <FontAwesomeIcon icon={faMap} className="me-2" />
                Map Search
              </Button>
            </ButtonGroup>
          </div>
          
          {/* Mode description */}
          <p className="mode-description">
            {searchMode === 'ai' 
              ? "Chat with our AI assistant to find properties that match your lifestyle"
              : "Search and filter properties using our interactive map"}
          </p>
        </Container>
      </div>

      {/* Content area */}
      <div className="search-content">
        {searchMode === 'ai' ? (
          <div className="ai-search-wrapper">
            <HomePageV3 
              initialQuery={initialQuery}
              onPropertiesFound={(properties, criteria) => {
                setSharedPropertyResults(properties);
                setSharedSearchCriteria(criteria);
              }}
              showMapButton={true}
              onViewMap={() => handleModeChange('map')}
            />
          </div>
        ) : (
          <div className="map-search-wrapper">
            <CustomerPortal />
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchWithAI;